'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';
import {
  type ComponentProps,
  type CSSProperties,
  type FocusEvent,
  type HTMLAttributes,
  type MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { cn } from '../utils';
import { withReleaseBodyOnClose } from '../utils/releaseBodyOnClose';

function shouldBlurActiveElementForKeyboardDismiss(node: Element): boolean {
  if (!(node instanceof HTMLElement)) return false;
  if (node.isContentEditable && node.getAttribute('contenteditable') === 'true') {
    return true;
  }
  if (node instanceof HTMLTextAreaElement || node instanceof HTMLSelectElement) {
    return true;
  }
  if (node instanceof HTMLInputElement) {
    const type = node.type;
    return !['button', 'checkbox', 'file', 'hidden', 'image', 'radio', 'reset', 'submit'].includes(
      type,
    );
  }
  return false;
}

const KEYBOARD_SAFE_PADDING_PX = 16;
/** iOS can deliver `scroll` on the dialog after microtasks; ignore blur-on-scroll until this far past each keyboard reflow. */
const SCROLL_BLUR_SUPPRESS_MS = 900;
/** Android Chrome can synthesize pointer events outside the dialog during keyboard-driven viewport reflow; ignore `onPointerDownOutside` for this long after focus enters a textual field or the visual viewport resizes. */
const OUTSIDE_CLOSE_SUPPRESS_MS = 1500;

interface VisualViewportMetrics {
  overlapPx: number;
  height: number | null;
  offsetTop: number;
}

function readVisualViewportMetrics(): VisualViewportMetrics {
  const vv = window.visualViewport;
  if (!vv) return { overlapPx: 0, height: null, offsetTop: 0 };
  return {
    overlapPx: Math.max(0, window.innerHeight - vv.height - vv.offsetTop),
    height: vv.height,
    offsetTop: vv.offsetTop,
  };
}

/** Scroll `scrollContainer` so `focused` lies in the intersection of the panel's on-screen rect and the visual viewport (keyboard). Without the panel clip, fields below the dialog's scrollport still matched `visualViewport` and got delta 0. */
function scrollFocusedElementIntoVisibleRegion(scrollContainer: HTMLElement, focused: HTMLElement) {
  const pad = KEYBOARD_SAFE_PADDING_PX;
  const fr = focused.getBoundingClientRect();
  const cr = scrollContainer.getBoundingClientRect();

  let portTop = cr.top + pad;
  let portBottom = cr.bottom - pad;
  const vv = window.visualViewport;
  if (vv) {
    const vvTop = vv.offsetTop + pad;
    const vvBottom = vv.offsetTop + vv.height - pad;
    portTop = Math.max(portTop, vvTop);
    portBottom = Math.min(portBottom, vvBottom);
  }
  if (portBottom <= portTop) {
    portTop = cr.top + pad;
    portBottom = cr.bottom - pad;
  }

  let delta = 0;
  if (fr.bottom > portBottom) {
    delta = fr.bottom - portBottom;
  } else if (fr.top < portTop) {
    delta = -(portTop - fr.top);
  }
  if (delta !== 0) {
    const maxScroll = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
    scrollContainer.scrollTop = Math.min(maxScroll, Math.max(0, scrollContainer.scrollTop + delta));
  }
}

const Dialog = withReleaseBodyOnClose(DialogPrimitive.Root);

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = ({ className, ...props }: ComponentProps<typeof DialogPrimitive.Overlay>) => (
  <DialogPrimitive.Overlay
    className={cn(
      'bg-background/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 backdrop-blur-sm data-[state=closed]:!pointer-events-none',
      className,
    )}
    {...props}
  />
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogCloseCustom = ({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Close>) => (
  <DialogPrimitive.Close
    className={cn(
      'fixed right-4 top-4 z-50 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none',
      className,
    )}
    {...props}
  >
    <Cross2Icon className="h-4 w-4" />
    <span className="sr-only">Close</span>
  </DialogPrimitive.Close>
);

function DialogContent({
  className,
  children,
  showOverlay = true,
  overlayClassName,
  showClose = true,
  onOverlayClick,
  disableOutsideClick = false,
  autoFocusOnOpen = false,
  onOpenAutoFocus,
  blurActiveElementOnScroll = true,
  onScroll,
  keepFocusVisibleAboveKeyboard = true,
  onFocus,
  onBlur,
  style: styleProp,
  ...props
}: ComponentProps<typeof DialogPrimitive.Content> & {
  showClose?: boolean;
  onOverlayClick?: MouseEventHandler<HTMLDivElement>;
  showOverlay?: boolean;
  /** Extra classes for the backdrop, e.g. a plain dim instead of the default wash and blur. */
  overlayClassName?: string;
  disableOutsideClick?: boolean;
  /** When true, Radix will focus the first tabbable element inside the dialog on open (previous default). */
  autoFocusOnOpen?: boolean;
  /**
   * When true, scrolling this panel (only when it is the scroll container — scroll does not bubble from nested
   * overflow regions) blurs textual fields so the software keyboard can dismiss on mobile.
   */
  blurActiveElementOnScroll?: boolean;
  /**
   * When true, textual fields scroll this panel so the control stays in view: the intersection of this node's
   * client rect and `window.visualViewport` (keyboard on mobile). Adds dynamic bottom padding from keyboard overlap
   * so the last fields can scroll above the keyboard. Only effective when this content node is the scroll container
   * (`overflow: auto|scroll` here, not only on a child).
   */
  keepFocusVisibleAboveKeyboard?: boolean;
}) {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const vvCleanupRef = useRef<(() => void) | null>(null);
  /** `performance.now()` deadline; blur-on-scroll is skipped while earlier (covers deferred scroll after scrollTop writes). */
  const suppressScrollBlurUntilRef = useRef(0);
  /** `performance.now()` deadline; `onPointerDownOutside` is ignored while earlier so Android Chrome's synthesized outside-events during keyboard reflow don't close the dialog. */
  const suppressOutsideCloseUntilRef = useRef(0);
  const [vvMetrics, setVvMetrics] = useState<VisualViewportMetrics>(() => ({
    overlapPx: 0,
    height: null,
    offsetTop: 0,
  }));
  const keyboardOverlapPx = vvMetrics.overlapPx;

  const clearVisualViewportListeners = useCallback(() => {
    vvCleanupRef.current?.();
    vvCleanupRef.current = null;
  }, []);

  const runKeyboardSafeScroll = useCallback(() => {
    if (!keepFocusVisibleAboveKeyboard) return;
    const container = contentRef.current;
    const active = document.activeElement;
    if (!container || !(active instanceof HTMLElement)) return;
    if (!container.contains(active)) return;
    if (!shouldBlurActiveElementForKeyboardDismiss(active)) return;
    suppressScrollBlurUntilRef.current = performance.now() + SCROLL_BLUR_SUPPRESS_MS;
    scrollFocusedElementIntoVisibleRegion(container, active);
  }, [keepFocusVisibleAboveKeyboard]);

  const scheduleKeyboardSafeScroll = useCallback(() => {
    const run = () => runKeyboardSafeScroll();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        run();
        window.setTimeout(run, 50);
        window.setTimeout(run, 150);
        window.setTimeout(run, 400);
      });
    });
  }, [runKeyboardSafeScroll]);

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLDivElement>) => {
      onFocus?.(e);
      if (!keepFocusVisibleAboveKeyboard) return;
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      if (!shouldBlurActiveElementForKeyboardDismiss(target)) return;

      suppressOutsideCloseUntilRef.current = Math.max(
        suppressOutsideCloseUntilRef.current,
        performance.now() + OUTSIDE_CLOSE_SUPPRESS_MS,
      );

      clearVisualViewportListeners();
      const vv = window.visualViewport;
      if (vv) {
        const onVvChange = () => {
          suppressOutsideCloseUntilRef.current = Math.max(
            suppressOutsideCloseUntilRef.current,
            performance.now() + OUTSIDE_CLOSE_SUPPRESS_MS,
          );
          runKeyboardSafeScroll();
        };
        vv.addEventListener('resize', onVvChange);
        vv.addEventListener('scroll', onVvChange);
        vvCleanupRef.current = () => {
          vv.removeEventListener('resize', onVvChange);
          vv.removeEventListener('scroll', onVvChange);
        };
      }
      scheduleKeyboardSafeScroll();
    },
    [
      clearVisualViewportListeners,
      keepFocusVisibleAboveKeyboard,
      onFocus,
      runKeyboardSafeScroll,
      scheduleKeyboardSafeScroll,
    ],
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLDivElement>) => {
      onBlur?.(e);
      if (!keepFocusVisibleAboveKeyboard) return;
      window.setTimeout(() => {
        const container = contentRef.current;
        const active = document.activeElement;
        if (!container || !active || !container.contains(active)) {
          clearVisualViewportListeners();
        }
      }, 0);
    },
    [clearVisualViewportListeners, keepFocusVisibleAboveKeyboard, onBlur],
  );

  useEffect(() => () => clearVisualViewportListeners(), [clearVisualViewportListeners]);

  useEffect(() => {
    if (!keepFocusVisibleAboveKeyboard) {
      setVvMetrics({ overlapPx: 0, height: null, offsetTop: 0 });
      return;
    }
    const update = () => setVvMetrics(readVisualViewportMetrics());
    update();
    const vv = window.visualViewport;
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [keepFocusVisibleAboveKeyboard]);

  useEffect(() => {
    if (!keepFocusVisibleAboveKeyboard) return;
    const id = requestAnimationFrame(() => runKeyboardSafeScroll());
    return () => cancelAnimationFrame(id);
  }, [keyboardOverlapPx, keepFocusVisibleAboveKeyboard, runKeyboardSafeScroll]);

  const isKeyboardUp = keepFocusVisibleAboveKeyboard && vvMetrics.overlapPx > 0;
  const mergedStyle: CSSProperties | undefined = keepFocusVisibleAboveKeyboard
    ? {
        ...(styleProp ?? {}),
        // Only override paddingBottom via inline style when the keyboard is actually visible.
        // When no keyboard, CSS (pb-6 default or pb-0 override) controls the padding so
        // sticky footers can sit flush with the dialog edge.
        ...(isKeyboardUp
          ? {
              paddingBottom: `calc(1.5rem + ${keyboardOverlapPx + KEYBOARD_SAFE_PADDING_PX}px + env(safe-area-inset-bottom, 0px))`,
            }
          : {}),
        // When the soft keyboard is up, clamp the panel to the visual viewport and re-center it
        // there. Without this, consumers' `max-h-[90vh]` (computed from layout viewport) leaves the
        // bottom of the panel underneath the keyboard on tablets and large phones.
        ...(isKeyboardUp && vvMetrics.height != null
          ? {
              maxHeight: `${vvMetrics.height}px`,
              top: `${vvMetrics.offsetTop + vvMetrics.height / 2}px`,
            }
          : {}),
      }
    : styleProp;

  return (
    <DialogPortal>
      {showOverlay && <DialogOverlay className={overlayClassName} onClick={onOverlayClick} />}
      <DialogPrimitive.Content
        ref={contentRef}
        className={cn(
          'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed left-[50%] top-[50%] z-50 grid max-h-svh w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-auto border shadow-lg duration-200 data-[state=closed]:!pointer-events-none sm:rounded-lg',
          keepFocusVisibleAboveKeyboard ? 'px-6 pb-6 pt-6' : 'p-6',
          className,
        )}
        onPointerDownOutside={(e) => {
          if (disableOutsideClick) {
            e.preventDefault();
            return;
          }
          // Android Chrome synthesizes pointer events outside the panel during keyboard-driven
          // viewport reflow; swallow those so a tap on a focused field doesn't close the dialog.
          if (performance.now() < suppressOutsideCloseUntilRef.current) {
            e.preventDefault();
          }
        }}
        onOpenAutoFocus={(e) => {
          onOpenAutoFocus?.(e);
          if (!autoFocusOnOpen) {
            e.preventDefault();
          }
        }}
        onScroll={(e) => {
          onScroll?.(e);
          if (!blurActiveElementOnScroll) return;
          if (performance.now() < suppressScrollBlurUntilRef.current) return;
          if (e.target !== e.currentTarget) return;
          const root = e.currentTarget;
          const active = document.activeElement;
          if (!active || !root.contains(active)) return;
          if (!shouldBlurActiveElementForKeyboardDismiss(active)) return;
          (active as HTMLElement).blur();
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...props}
        style={mergedStyle}
      >
        {children}
        {showClose && <DialogCloseCustom onClick={(e) => e.stopPropagation()} />}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = ({ className, ...props }: ComponentProps<typeof DialogPrimitive.Title>) => (
  <DialogPrimitive.Title
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = ({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) => (
  <DialogPrimitive.Description
    className={cn('text-muted-foreground text-sm', className)}
    {...props}
  />
);
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogCloseCustom,
  DialogTitle,
  DialogDescription,
};
